import { useState, useMemo } from 'react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Lightning, Shuffle, Check, X } from '@phosphor-icons/react';

const CATEGORIES = [
    { key: 'bediening_voertuig', icon: '🚗', name: 'Bediening Voertuig' },
    { key: 'verkeersdeelneming', icon: '🚦', name: 'Verkeersdeelneming' },
    { key: 'bijzondere_verrichtingen', icon: '🅿️', name: 'Bijzondere Verrichtingen' },
];

const SCORE_OPTIONS = [
    { value: 1, label: '1', color: 'bg-red-600 hover:bg-red-500' },
    { value: 2, label: '2', color: 'bg-orange-600 hover:bg-orange-500' },
    { value: 3, label: '3', color: 'bg-yellow-600 hover:bg-yellow-500' },
    { value: 4, label: '4', color: 'bg-lime-600 hover:bg-lime-500' },
    { value: 5, label: '5', color: 'bg-emerald-600 hover:bg-emerald-500' },
];

function randomInRange(min, max) {
    return Math.floor(Math.random() * (max - min + 1)) + min;
}

/**
 * Bulk-set scores per category. Allows instructor to quickly set a starting
 * level for students who already had lessons elsewhere. The result is written
 * into `editedScores` (parent state) but NOT auto-saved — instructor still
 * clicks "Opslaan" to commit.
 */
export default function BulkScoreModal({ open, onClose, skills, currentScores, onApply }) {
    const [draft, setDraft] = useState(currentScores || {});

    // Skills grouped by category (memoized)
    const grouped = useMemo(() => {
        const out = {};
        for (const cat of CATEGORIES) out[cat.key] = [];
        for (const s of skills || []) {
            if (out[s.category]) out[s.category].push(s);
        }
        return out;
    }, [skills]);

    const setCategoryAll = (catKey, value) => {
        const next = { ...draft };
        for (const s of grouped[catKey]) next[s.id] = value;
        setDraft(next);
    };

    const setCategoryRandom = (catKey, min, max) => {
        const next = { ...draft };
        for (const s of grouped[catKey]) next[s.id] = randomInRange(min, max);
        setDraft(next);
    };

    const overrideOne = (skillId, value) => {
        setDraft(prev => ({ ...prev, [skillId]: value }));
    };

    const handleApply = () => {
        onApply(draft);
        onClose();
    };

    const handleReset = () => {
        setDraft(currentScores || {});
    };

    return (
        <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
            <DialogContent className="bg-[#2D3748] border-[#4A5568] text-white max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="bulk-score-modal">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        <Lightning size={22} weight="fill" className="text-[#D4A537]" />
                        Snel scores instellen
                    </DialogTitle>
                    <DialogDescription className="text-gray-400">
                        Ideaal voor overstappers of leerlingen die al lessen hebben gehad. Stel per categorie in één klik scores in, daarna kun je individuele vaardigheden nog finetunen. Klik "Toepassen" en daarna "Opslaan" op de leskaart.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-5 py-2">
                    {CATEGORIES.map(cat => {
                        const catSkills = grouped[cat.key] || [];
                        if (!catSkills.length) return null;
                        return (
                            <section key={cat.key} className="bg-[#1A202C] rounded-lg p-4 border border-[#4A5568]" data-testid={`bulk-cat-${cat.key}`}>
                                <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                                    <div className="flex items-center gap-2">
                                        <span className="text-xl" aria-hidden>{cat.icon}</span>
                                        <h4 className="font-semibold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                                            {cat.name}
                                        </h4>
                                        <span className="text-xs text-gray-500">({catSkills.length} vaardigheden)</span>
                                    </div>
                                    <div className="flex flex-wrap gap-1.5 items-center">
                                        {SCORE_OPTIONS.map(opt => (
                                            <button
                                                key={opt.value}
                                                onClick={() => setCategoryAll(cat.key, opt.value)}
                                                className={`${opt.color} text-white font-semibold w-8 h-8 rounded-md text-sm transition-colors`}
                                                title={`Alles op ${opt.value}`}
                                                data-testid={`bulk-set-${cat.key}-${opt.value}`}
                                            >
                                                {opt.label}
                                            </button>
                                        ))}
                                        <span className="text-gray-500 mx-1">|</span>
                                        <button
                                            onClick={() => setCategoryRandom(cat.key, 3, 5)}
                                            className="bg-[#3D4A5C] hover:bg-[#4A5568] text-white text-xs font-medium px-2.5 h-8 rounded-md inline-flex items-center gap-1 transition-colors"
                                            title="Random tussen 3 en 5"
                                            data-testid={`bulk-random-${cat.key}-3-5`}
                                        >
                                            <Shuffle size={12} /> 3-5
                                        </button>
                                        <button
                                            onClick={() => setCategoryRandom(cat.key, 2, 4)}
                                            className="bg-[#3D4A5C] hover:bg-[#4A5568] text-white text-xs font-medium px-2.5 h-8 rounded-md inline-flex items-center gap-1 transition-colors"
                                            title="Random tussen 2 en 4"
                                            data-testid={`bulk-random-${cat.key}-2-4`}
                                        >
                                            <Shuffle size={12} /> 2-4
                                        </button>
                                    </div>
                                </div>

                                {/* Per-skill override grid */}
                                <ul className="space-y-1.5">
                                    {catSkills.map(s => {
                                        const v = draft[s.id] ?? 0;
                                        return (
                                            <li key={s.id} className="flex items-center gap-2 text-sm">
                                                <span className="flex-1 text-gray-200 truncate">{s.name}</span>
                                                <div className="flex gap-1">
                                                    {SCORE_OPTIONS.map(opt => (
                                                        <button
                                                            key={opt.value}
                                                            onClick={() => overrideOne(s.id, opt.value)}
                                                            className={`w-7 h-7 rounded text-xs font-semibold transition-all ${v === opt.value ? opt.color + ' text-white ring-2 ring-white/30' : 'bg-[#2D3748] hover:bg-[#3D4A5C] text-gray-400'}`}
                                                            data-testid={`bulk-skill-${s.id}-${opt.value}`}
                                                        >
                                                            {opt.label}
                                                        </button>
                                                    ))}
                                                </div>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </section>
                        );
                    })}
                </div>

                <div className="flex justify-between items-center pt-3 border-t border-[#4A5568]">
                    <Button
                        variant="ghost"
                        onClick={handleReset}
                        className="text-gray-400 hover:text-white"
                        data-testid="bulk-reset-button"
                    >
                        Reset
                    </Button>
                    <div className="flex gap-2">
                        <Button
                            variant="outline"
                            onClick={onClose}
                            className="border-[#4A5568] text-gray-300 hover:bg-[#3D4A5C] hover:text-white"
                            data-testid="bulk-cancel-button"
                        >
                            <X size={16} className="mr-1" /> Annuleren
                        </Button>
                        <Button
                            onClick={handleApply}
                            className="bg-[#D4A537] hover:bg-[#E5B84A] text-[#1A202C] font-semibold"
                            data-testid="bulk-apply-button"
                        >
                            <Check size={16} className="mr-1" /> Toepassen
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
